<script setup lang="ts">
import CodeMirror from "vue-codemirror6";
import { sql, SQLite } from "@codemirror/lang-sql";

const isDialogVisible = defineModel<boolean>();
const emit = defineEmits(["update"]);
import { computed, PropType, ref, toRefs } from "vue";
import {
	getCellAccessorKey,
	mapDimensionIndexToName,
	VALUE_FORMATS,
} from "@/modules/explorer/components/Metrics/MetricHelper";
import { SQLConfig } from "@codemirror/lang-sql";
import { useExplorerStore } from "@/modules/explorer/store";

const explorerStore = useExplorerStore();
const props = defineProps({
	dataTableToEdit: {
		type: Object as PropType<App.Data.Explorer.DataTableDO>,
		required: true,
	},
});

const comparisonPeriods = computed(() => {
	return [
		explorerStore.document.date_comparison.comparisonLabel || "Y",
		explorerStore?.derivedDocument.date_comparison.isBudgetComparisonActive
			? "Bud"
			: null,
		...explorerStore.document.date_comparison.periods.map(
			(period) => `Y${period}`
		),
		...explorerStore.document.date_comparison.customPeriods.map(
			(period) => period.name
		),
	]
		.filter(Boolean)
		.sort((a, b) => {
			if (a === "Bud") return 1;
			if (b === "Bud") return -1;
			return a.localeCompare(b, undefined, { numeric: true });
		});
});

const customSql = ref(props.dataTableToEdit?.custom_sql ?? "");

const { dataTableToEdit } = toRefs(props);
const allowedBudgetMetrics = [
	"revenuesdriveout",
	"revenuesdrivein",
	"advertising_revenue_distr",
];
const accessorKeys = () => {
	const dimensionAccessorKeys = [];
	dataTableToEdit.value.dimensions.forEach((dimension, idx) => {
		dimensionAccessorKeys.push(mapDimensionIndexToName(idx));
	});

	const metricAccessorKeys = [];

	dataTableToEdit.value.metrics.forEach((metric) => {
		comparisonPeriods.value.forEach((comparison) => {
			if (
				comparison === "Bud" &&
				!allowedBudgetMetrics.includes(metric.name_clean)
			) {
				return;
			}
			if (dataTableToEdit.value.split_prediction) {
				if (comparison === "Y") {
					metricAccessorKeys.push(
						getCellAccessorKey(metric, "actualValue", comparison)
					);
					metricAccessorKeys.push(
						getCellAccessorKey(metric, "totalValue", comparison)
					);
				} else {
					metricAccessorKeys.push(
						getCellAccessorKey(metric, "totalValue", comparison)
					);
				}
			} else {
				metricAccessorKeys.push(
					getCellAccessorKey(metric, "totalValue", comparison)
				);
			}
			if (comparison !== "Y") {
				if (dataTableToEdit.value.show_change_absolute) {
					metricAccessorKeys.push(
						getCellAccessorKey(metric, "deltaAbsolute", comparison)
					);
				}
				if (dataTableToEdit.value.show_change_relative_to_current) {
					metricAccessorKeys.push(
						getCellAccessorKey(metric, "deltaRelative", comparison)
					);
				}
			}
		});
	});
	return dimensionAccessorKeys.concat(metricAccessorKeys).sort();
};

const applyCustomSql = () => {
	dataTableToEdit.value.custom_sql = customSql.value;
	emit("update", dataTableToEdit.value);
	isDialogVisible.value = false;
};

const clearCustomSql = () => {
	customSql.value = "";
};

const showInstructions = ref(false);

const sqlConfig: SQLConfig = {
	dialect: SQLite,
	tables: [
		{
			label: "result",
			type: "table",
			info: "Main table",
		},
	],
	schema: {
		result: [
			...accessorKeys(),
			{
				label: "apply_hierarchy",
				type: "function",
				detail:
					"(string value, string dimension_name, int hierarchy_id, int depth) → string",
				info: "Returns the hierarchy name at the given depth",
			},
			{
				label: "asset_name",
				type: "function",
				detail:
					"(string value, string dimension_name, int hierarchy_id, int depth) → string",
				info: "Returns the asset name for the given dimension/hierarchy combination.",
			},
		],
	},
	defaultTable: "result",
	upperCaseKeywords: false,
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" width="70vw">
		<v-card elevation="9">
			<v-toolbar color="secondary" flat>
				<template #prepend>
					<v-icon class="ml-3 mr-5">mdi-database-edit-outline</v-icon>
					<v-toolbar-title class="pt-1">Custom SQL</v-toolbar-title>
				</template>
				<template #append>
					<v-btn icon @click="isDialogVisible = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</template>
			</v-toolbar>
			<v-divider />
			<perfect-scrollbar
				class="w-100"
				style="overflow-x: hidden; max-height: 80vh"
				:options="{ suppressScrollX: true }"
			>
				<div class="pa-5">
					<code-mirror basic :lang="sql(sqlConfig)" v-model="customSql" />

					<div class="mt-4">
						<v-btn
							variant="text"
							size="small"
							:append-icon="
								showInstructions ? 'mdi-chevron-up' : 'mdi-chevron-down'
							"
							class="px-1"
							@click="showInstructions = !showInstructions"
						>
							Instructions
						</v-btn>

						<v-expand-transition>
							<div v-if="showInstructions">
								<v-table
									density="compact"
									class="instructions-table mt-1 rounded border"
								>
									<tbody>
										<tr>
											<td class="text-medium-emphasis">Table name</td>
											<td class="pa-2"><code>result</code></td>
										</tr>
										<tr>
											<td class="text-medium-emphasis">Value type suffix</td>
											<td class="pa-2">
												Append <code>_{type}</code> to a column name to set its
												format — e.g. <code>revenue_Y_totalValue_money</code>.
												The suffix is stripped from the display name. Dimension
												columns are auto-detected and need no suffix.
											</td>
										</tr>
										<tr>
											<td class="text-medium-emphasis">Row styling</td>
											<td class="pa-2">
												Add a <code>rowClass</code> column with CSS class names.
												<code class="d-block mt-1"
													>CASE WHEN ROW_NUMBER() OVER () = 1 THEN
													'hierarchy-parent' END AS rowClass</code
												>
											</td>
										</tr>
										<tr>
											<td class="text-medium-emphasis">Value types</td>
											<td class="pa-2">
												<template
													v-for="(format, i) in VALUE_FORMATS"
													:key="format"
												>
													<code>{{ format }}</code
													>{{ i < VALUE_FORMATS.length - 1 ? ",  " : "" }}
												</template>
											</td>
										</tr>
									</tbody>
								</v-table>
							</div>
						</v-expand-transition>
					</div>
				</div>
			</perfect-scrollbar>
			<v-divider />
			<v-card-actions class="pa-3">
				<v-spacer />
				<div class="d-flex ga-2">
					<v-btn variant="text" @click="clearCustomSql">Clear</v-btn>
					<v-btn
						variant="flat"
						prepend-icon="mdi-import"
						@click="applyCustomSql"
						>Apply</v-btn
					>
				</div>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
.instructions-table {
	td:first-child {
		white-space: nowrap;
		width: 1%;
		padding-right: 1.5rem;
		vertical-align: middle;
	}

	td {
		vertical-align: middle;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
	}

	code {
		background-color: rgba(var(--v-theme-on-surface), 0.06);
		border-radius: 4px;
		padding: 0.3em 0.4em;
		font-size: 0.9em;
		margin: 4px 0px;
	}
}
</style>
